import React, { ReactNode } from 'react';
import { CustomIcon, FavouriteIcon } from '@tymbe/ui-icons';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { QueryFunctionContext, useQuery } from 'react-query';
import apiClient, { ServiceTypes } from '../../../apiClient';
import AvatarWithInitials from '../../../components/AvatarWithInitials';

interface TymberInfoBoxProps {
  personId: number;
  icon?: ReactNode;
  isCompanyJunior?: boolean;
}

const queryFn = <S extends keyof ServiceTypes>({
  queryKey: [service, id],
}: QueryFunctionContext<readonly [S, ServiceTypes[S]['id']]>): ReturnType<
  ServiceTypes[S]['get']
> => {
  return apiClient.service(service).get(id) as ReturnType<
    ServiceTypes[S]['get']
  >;
};

const TymberInfoBox = ({
  personId,
  icon,
  isCompanyJunior,
}: TymberInfoBoxProps) => {
  const { data: person } = useQuery({
    queryKey: ['person', personId] as const,
    queryFn,
    enabled: !!personId,
  });

  const { data: personData } = useQuery({
    queryKey: ['person-data', personId] as const,
    queryFn,
    enabled: !!personId,
  });

  return (
    <Link
      to={`/user/${person?.id}`}
      target="blank"
      className="flex gap-2 items-center hover:bg-secondary-transparent-5 px-2 py-1 rounded-lg"
    >
      <AvatarWithInitials initials={person?.first_name[0] || ''} icon={icon} />
      <div className="">
        <div className="text-secondary-600 font-medium text-body-2-xl">
          {person?.first_name} {person?.last_name}
        </div>
        <div className="flex text-secondary-400 text-xs items-center">
          <FavouriteIcon
            name={personData?.gender || 'female'}
            className="size-4"
          />
          <span className="text-secondary-500 text-caption-xl align-middle pb-0">
            {moment().diff(personData?.birthdate, 'years')}
            <span className="text-secondary-transparent-16"> • </span>
            {person?.id}
          </span>
          {isCompanyJunior ? (
            <div className='flex ml-1'>
              <span className="text-secondary-transparent-16"> • </span>
              <CustomIcon name="rookie" className="size-4" />
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  );
};

export default TymberInfoBox;
